import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import {Identifier} from "../common/Identifier";
import {SoftDeletable} from "../common/SoftDeletable";
import {IdAndValue} from "../common/IdAndValue";

/**
 * Represents the name of the system area within the application.
 *
 * This variable is intended to standardize the identification of a core system-related area.
 * It may be used across various modules for referencing the system's namespace or grouping logic
 * that pertains to system-level functionality.
 *
 * Technical Details:
 * - The value of this variable is a string representing the system area name.
 * - Ensure consistency when utilizing this variable for comparisons or assignments
 *   to avoid hardcoding similar values elsewhere in the application logic.
 * - Changing this value could have downstream effects; confirm all references are updated as needed.
 */
export const systemAreaName = "system";
/**
 * Represents a module for managing user groups within the system.
 *
 * This module centralizes logic related to user group operations,
 * such as verifying user privileges within the context of the "groups" system area.
 */
export const UserGroupModule: Module = {
    identifier: "groups",
    isPrivileged: (authUser, privilege) =>
        isPrivileged( authUser, systemAreaName, UserGroupModule.identifier, privilege )
};

/**
 * Represents the unique identifier for a user group in the system.
 *
 * This interface is used to encapsulate and standardize the handling of user group identifiers.
 * The `userGroupId` provides a type-safe mechanism to distinguish user group IDs from other types
 * of identifiers. It is particularly useful to ensure consistency across APIs and database operations.
 */
export interface UserGroupId {
    userGroupId: Identifier<number>
}

/**
 * Represents an identifier used to associate privileges with a user group.
 *
 * This type is used to uniquely define and reference the privileges assigned to a specific user group within the system. It ensures consistency in privilege identification across various services or components.
 *
 * @typedef UserGroupPrivilegeIdentifier
 * @type {Identifier<string>}
 */
export type UserGroupPrivilegeIdentifier = Identifier<string>

/**
 * Represents and manages a group of users with associated permissions and areas of responsibility.
 *
 * This interface is used to define user groups that facilitate role-based access control
 * by associating users with specific privileges and areas within a system. It also supports
 * soft deletion for safe data handling.
 */
export interface UserGroup extends UserGroupId, SoftDeletable {

    userGroupDisplayName: string
    associatedAreas: UserGroupAssociatedArea[]
    privileges: { [ key: string ]: UserGroupPrivilege }
    isDataBased: boolean
    users?: IdAndValue<number, string>[]
}

/**
 * Represents the association of a user group with a specific privilege and its permission state.
 *
 * This interface is used to define whether a particular user group has access to specific privileges
 * within the system. It helps ensure access control is implemented clearly and can be used for
 * authorization checks and privilege evaluations.
 */
export interface UserGroupPrivilege {
    userGroupId: Identifier<number>
    privilegeIdentifier: UserGroupPrivilegeIdentifier
    isPermitted: boolean
}

/**
 * Represents an area associated with a user group, including its details
 * and related privileges within the application.
 *
 * This interface is used to describe a specific logical or geographical area
 * tied to a user group. It includes metadata about the area, its display name,
 * and a list of privileges applicable to the user group in the context of this area.
 * This interface supports scenarios such as role-based access control (RBAC).
 *
 * @property areaId - The unique identifier of the area associated with the user group.
 * @property areaName - The programmatic name of the area, often used internally by developers.
 * @property areaDisplayName - The human-readable name of the area, meant for display in UI/UX.
 * @property privileges - An optional list of privileges granted to this user group for the area.
 *                        If null, it indicates either no privileges are assigned or the area is
 *                        not actively managed.
 * @property deleted - Indicates whether the area is marked as deleted. This is used for soft deletions,
 *                     where the record may not be removed from the database but is flagged as inactive.
 */
export interface UserGroupAssociatedArea {
    areaId: Identifier< number >
    areaName: string
    areaDisplayName: string
    privileges: null | UserGroupAssociatedAreaPrivilege[]
    deleted: boolean    
}

/**
 * Represents the association of a privilege with a specific user group and area within the system.
 *
 * This interface is used to define and manage what actions or views a user group is authorized to access
 * within a specific section of the application. It allows both technical teams and stakeholders to identify
 * and adjust user group permissions efficiently.
 */
export interface UserGroupAssociatedAreaPrivilege {
    id: Identifier<number>
    privilegeIdentifier: string
    viewIdentifier: string
    displayTitle: string

    deleted: boolean
    permitted: boolean    
}